import { pick } from 'lodash';

import { CLONE_ITEM, buildBaseClone } from '@/utilities/orderClone';

// order_status of an attempt-billing clone: a terminal order that bills exactly ONE
// delivery attempt (priced to the attempt fee only — no base/other charges).
export const FAILED_ORDER_STATUS = 'failed';

const isAttemptEvent = (event) => (event?.action || '').includes('ATTEMPT');

// The attempt an exception is about. The exception records its own event (exceptions.event_id and
// each mapping's event_id), so bill that one. The latest still-billable attempt is only a fallback
// for exceptions written without an event link.
export const attemptForException = (order, exception) => {
    const attempts = (order?.wh_events || []).filter(isAttemptEvent);
    const linkedIds = new Set(
        [exception?.event_id, ...(exception?.mappings || []).map((m) => m?.event_id)].filter(Boolean)
    );
    const linked = attempts.find((e) => linkedIds.has(e.event_id));
    if (linked) return linked;
    return [...attempts].reverse().find((e) => e.billable !== false) || attempts.slice(-1)[0];
};

// Mark every attempt event except the one being billed as non-billable, so the order
// prices to a SINGLE attempt fee regardless of how many billable attempts it carries.
// Used for the in-memory price() input (keeps all original event fields intact).
export const singleAttemptEvents = (order, currentAttempt) =>
    (order?.wh_events || []).map((event) =>
        isAttemptEvent(event) && event.event_id !== currentAttempt?.event_id ? { ...event, billable: false } : event
    );

// wh_events for the billing clone: the order's full history (the DB validates each event as a
// transition from the one before, so no event can be dropped), with exactly one attempt billable —
// the one being billed. The clone is priced from these rows, so it bills that single attempt.
// route_id is dropped so the clone's events don't attach to the original's route.
export const cloneWhEvents = (order, currentAttempt) =>
    (order?.wh_events || []).map((event) => ({
        action: event.action,
        status: event.status,
        driver_id: event.driver_id,
        route_id: null,
        billable: isAttemptEvent(event) ? event.event_id === currentAttempt?.event_id : true,
        created_at: event.created_at,
    }));

// An earlier "Bill this attempt" that inserted its clone but failed to price it (price_breakdown still
// null) for this same attempt. A retry reprices that clone instead of inserting a second one. Clone
// events copy the original's created_at, so the clone's billable attempt shares the attempt's timestamp.
export const findUnpricedClone = (clones, currentAttempt) => {
    const attemptTime = currentAttempt?.created_at && new Date(currentAttempt.created_at).getTime();
    if (!attemptTime) return undefined;
    return (clones || []).find(
        (clone) =>
            !clone?.price_breakdown &&
            (clone?.wh_events || []).some((e) => e?.created_at && new Date(e.created_at).getTime() === attemptTime)
    );
};

// Build the attempt-billing clone: a terminal "failed" order priced to a single attempt
export const buildFailedClone = ({ order, priced, currentAttempt, existingAttemptClones }) => ({
    ...buildBaseClone({ order, priced, suffixLetter: 'A', index: existingAttemptClones + 1 }),
    order_status: FAILED_ORDER_STATUS,
    // carried for driver-pay attribution + the daily-split day
    driver_id: currentAttempt?.driver_id || order.driver_id || null,
    itemsByOrderId: {
        data: (order.itemsByOrderId || []).map((item) => pick(item, CLONE_ITEM)),
    },
    wh_events: {
        data: cloneWhEvents(order, currentAttempt),
    },
});
